import React, { useEffect, useMemo } from 'react';
import { getNormalisedPerformanceFFs } from '@atlassian/jira-common-long-task-metrics/src/common/util/collectors';
import { SubmitApdex } from '@atlassian/jira-providers-spa-apdex-analytics/src';
import { pushEarlyMarksToBrowserMetrics } from '@atlassian/jira-software-browser-metrics';
import {
	BACKLOG,
	getAndClearInvalidationReasons,
} from '@atlassian/jira-software-resource-invalidator';
import {
	emitApdexOnAnimationFrame,
	isAlternateTTIMeasurementEnabled,
	isErrorTrackingEnabledWithSentry,
} from '../../feature-flags';
import {
	CRITICAL_RELAY_RESOURCE_GET_DATA_END,
	CRITICAL_RELAY_RESOURCE_GET_DATA_START,
	JSW_CRITICAL_RESOURCE_END,
	JSW_CRITICAL_RESOURCE_START,
	RENDER_END,
	RENDER_START,
	USE_RESOURCE_READY,
	USE_RESOURCE_START,
} from '../../services/utils/performance-analytics';
import { useBacklogSelector } from '../../state';
import { getProjectKey, getRapidViewId, getSessionId } from '../../state/selectors/app';
import {
	cardsSelector,
	getCardListStats,
	numTotalIssueChildren,
} from '../../state/selectors/cards';
import { issueParentIdsSelector } from '../../state/selectors/issue-parent';
import { getWhiteboardEligibility } from '../../state/selectors/whiteboards-entrypoint-experiment';
import { useIsCMPBacklog } from '../../state/state-hooks/capabilities';
import { usePerformanceMetric } from '../../state/state-hooks/performance-metric';

export type OwnProps = {
	isCacheHit: boolean;
};

type Props = OwnProps;

// Also fire BM3 metric.mark() for custom timings, on top of performance.mark() invocations
const SubmitApdexContainer = ({ isCacheHit }: Props) => {
	const issueNo = useBacklogSelector((state) => cardsSelector(state).length);
	const epicsNo = useBacklogSelector((state) => issueParentIdsSelector(state).length);
	const issueChildrenNo = useBacklogSelector(numTotalIssueChildren);
	const cardListCounts = useBacklogSelector(getCardListStats);
	const backlogSessionId = useBacklogSelector(getSessionId);
	const boardId = useBacklogSelector(getRapidViewId);
	const projectKey = useBacklogSelector(getProjectKey);
	const metric = usePerformanceMetric();
	const isClassic = useIsCMPBacklog();
	const { cohort: whiteboardCohort } = useBacklogSelector(getWhiteboardEligibility);

	const extra = useMemo(
		() => ({
			...getNormalisedPerformanceFFs(),
			isCacheHit,
			cacheInvalidationReasons: getAndClearInvalidationReasons(BACKLOG).join(','),
			issueNo,
			epicsNo,
			issueChildrenNo,
			backlogSessionId,
			boardId,
			projectKey,
			...cardListCounts,
			isSentryEnabled: String(isErrorTrackingEnabledWithSentry()),
			isPrefetchIssueDataByRankEnabled: false,
			isUIF: true,
			isClassic,
			jswSprintPlanningWhiteboards: whiteboardCohort,
		}),
		[
			isCacheHit,
			issueNo,
			epicsNo,
			issueChildrenNo,
			backlogSessionId,
			boardId,
			projectKey,
			cardListCounts,
			isClassic,
			whiteboardCohort,
		],
	);

	useEffect(() => {
		pushEarlyMarksToBrowserMetrics(metric, {
			marks: {
				BACKLOG_FETCH_START: JSW_CRITICAL_RESOURCE_START,
				'jira.fe.spa-prefetchApiReady': JSW_CRITICAL_RESOURCE_END,
				USE_RESOURCE_START_MARK: USE_RESOURCE_START,
				USE_RESOURCE_READY_MARK: USE_RESOURCE_READY,
				'render:start': RENDER_START,
				'render:end': RENDER_END,
				'relay_resource_get_data:NEXTGEN_BACKLOG_RELAY:start':
					CRITICAL_RELAY_RESOURCE_GET_DATA_START,
				'relay_resource_get_data:NEXTGEN_BACKLOG_RELAY:end': CRITICAL_RELAY_RESOURCE_GET_DATA_END,
			},
		});
	}, [metric]);

	return (
		<SubmitApdex
			extra={extra}
			appName="software-backlog"
			// @ts-expect-error - TS2322 - Type 'PageLoadMetric' is not assignable to type 'BM3Metric'.
			metric={metric}
			emitOnAnimationFrame={emitApdexOnAnimationFrame()}
			waitForLongTaskIdleTTI={isAlternateTTIMeasurementEnabled()}
		/>
	);
};

export default SubmitApdexContainer;
